import { GlassCard } from '@/components/design-system';
import { Button } from '@/components/ui/button';
import {
  runSolBalanceQueryForCommonQueries,
  runUsdcBalanceQueryForCommonQueries,
} from '@/lib/collections/commonQueries';
import { cn, generateRandomId } from '@/lib/utils';
import { Coins, RefreshCw, Wallet } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

interface WalletBalanceCardProps {
  walletAddress: string;
}

const LAMPORTS_PER_SOL = 1_000_000_000;
const USDC_BASE_UNITS = 1_000_000; // 6 decimals

/** Live on-chain SOL + USDC balance for the connected household wallet, read directly from chain. */
export function WalletBalanceCard({ walletAddress }: WalletBalanceCardProps) {
  const [sol, setSol] = useState<number | null>(null);
  const [usdc, setUsdc] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchBalances = useCallback(async () => {
    setLoading(true);
    try {
      const [solLamports, usdcBaseUnits] = await Promise.all([
        runSolBalanceQueryForCommonQueries(generateRandomId(), { walletAddress }),
        runUsdcBalanceQueryForCommonQueries(generateRandomId(), { walletAddress }),
      ]);
      setSol(solLamports / LAMPORTS_PER_SOL);
      setUsdc(usdcBaseUnits / USDC_BASE_UNITS);
    } catch (err) {
      console.error('Failed to fetch wallet balances', err);
      toast.error('Could not load wallet balance right now.');
    } finally {
      setLoading(false);
    }
  }, [walletAddress]);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  return (
    <GlassCard padding="lg" className="h-full">
      <div className="flex items-center justify-between gap-3">
        <div>
          <span className="text-overline">Live Balance</span>
          <h2 className="font-heading mt-1 text-xl font-bold text-foreground">Devnet Wallet</h2>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={fetchBalances}
          disabled={loading}
          className="shrink-0 rounded-full"
          title="Refresh balance"
        >
          <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
        </Button>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-border/60 bg-secondary/30 p-4">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <Wallet className="h-3.5 w-3.5 text-primary" /> SOL
          </div>
          <p className="mt-2 text-2xl font-bold text-foreground">
            {sol === null ? '—' : sol.toLocaleString(undefined, { maximumFractionDigits: 4 })}
          </p>
        </div>
        <div className="rounded-xl border border-border/60 bg-secondary/30 p-4">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <Coins className="h-3.5 w-3.5 text-accent" /> USDC
          </div>
          <p className="mt-2 text-2xl font-bold text-foreground">
            {usdc === null ? '—' : usdc.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </p>
        </div>
      </div>
    </GlassCard>
  );
}

export default WalletBalanceCard;
